import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import MigrationBanner from '../components/MigrationBanner';
import { useAuth } from '../context/AuthContext';
import { ShiftProvider } from '../context/ShiftContext';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  LayoutDashboard, ClipboardList, Users, Bell, BookOpen,
  Shield, BookMarked, LogOut, Menu, X, Anchor, FileText,
  UserSquare2, Award, MoreHorizontal
} from 'lucide-react';

const navItems = [
  { to: '/',          icon: LayoutDashboard, label: 'Dashboard',    end: true },
  { to: '/priorities',icon: ClipboardList,   label: 'Priorities' },
  { to: '/personnel', icon: Users,           label: 'Personnel' },
  { to: '/selres',    icon: UserSquare2,     label: 'SELRES' },
  { to: '/reminders', icon: Bell,            label: 'Reminders' },
  { to: '/passdown',  icon: BookOpen,        label: 'Passdown' },
  { to: '/programs',  icon: Shield,          label: 'Programs' },
  { to: '/notebook',  icon: BookMarked,      label: 'Notebook' },
  { to: '/awards',    icon: Award,           label: 'Awards' },
  { to: '/evals',     icon: FileText,        label: 'Eval Builder' },
];

const BOTTOM_NAV   = navItems.slice(0, 4);
const OVERFLOW_NAV = navItems.slice(4);

function MobileLayout({ children }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleLogout = async () => { await logout(); navigate('/login'); };
  const navBtnStyle = (isActive) => ({
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: '3px', padding: '8px 4px',
    background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'none',
    color: isActive ? 'var(--gold)' : 'var(--text-secondary)',
    fontSize: '0.62rem', fontWeight: isActive ? 700 : 400, minHeight: '56px',
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--navy)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: '52px', minHeight: '52px', background: 'var(--navy-mid)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Anchor size={18} color="var(--gold)" />
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.05em' }}>LPO DASHBOARD</span>
        </div>
        <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', padding: '8px', cursor: 'pointer', borderRadius: '6px' }}>
          <LogOut size={18} />
        </button>
      </div>
      <main style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', WebkitOverflowScrolling: 'touch' }}>
        {children}
      </main>
      <nav className="mobile-bottom-nav" style={{ display: 'flex', background: 'var(--navy-mid)', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
        {BOTTOM_NAV.map(({ to, icon: Icon, label, end }) => (
          <NavLink key={to} to={to} end={end} style={({ isActive }) => navBtnStyle(isActive)}>
            <Icon size={20} /><span>{label}</span>
          </NavLink>
        ))}
        <button onClick={() => setDrawerOpen(true)} style={navBtnStyle(false)}>
          <MoreHorizontal size={20} /><span>More</span>
        </button>
      </nav>
      {drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100 }} onClick={() => setDrawerOpen(false)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} />
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'var(--navy-mid)', borderRadius: '16px 16px 0 0', padding: '12px 0 32px' }}>
            <div style={{ width: '36px', height: '4px', background: 'var(--border)', borderRadius: '2px', margin: '0 auto 16px' }} />
            {OVERFLOW_NAV.map(({ to, icon: Icon, label }) => (
              <NavLink key={to} to={to} onClick={() => setDrawerOpen(false)} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 24px',
                textDecoration: 'none', color: isActive ? 'var(--gold)' : 'var(--text)',
                background: isActive ? 'rgba(201,168,76,0.08)' : 'transparent',
                fontSize: '1rem', fontWeight: isActive ? 600 : 400,
              })}>
                <Icon size={20} />{label}
              </NavLink>
            ))}
            <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }} />
            <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 24px', background: 'none', border: 'none', color: 'var(--danger)', fontSize: '1rem', cursor: 'pointer', width: '100%' }}>
              <LogOut size={20} /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DesktopLayout({ children }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const handleLogout = async () => { await logout(); navigate('/login'); };
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--navy)' }}>
      <aside style={{ width: sidebarOpen ? '220px' : '64px', minWidth: sidebarOpen ? '220px' : '64px', background: 'var(--navy-mid)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', transition: 'width 0.2s, min-width 0.2s', overflow: 'hidden', zIndex: 10 }}>
        <div style={{ padding: '16px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', minHeight: '64px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, overflow: 'hidden' }}>
            <Anchor size={20} color="var(--gold)" style={{ flexShrink: 0 }} />
            {sidebarOpen && <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gold)', whiteSpace: 'nowrap', letterSpacing: '0.05em' }}>LPO DASHBOARD</span>}
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', padding: '4px', display: 'flex', borderRadius: '4px', cursor: 'pointer' }}>
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end} style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', textDecoration: 'none',
              color: isActive ? 'var(--gold)' : 'var(--text-secondary)',
              background: isActive ? 'rgba(201,168,76,0.08)' : 'transparent',
              borderLeft: isActive ? '3px solid var(--gold)' : '3px solid transparent',
              fontSize: '0.875rem', fontWeight: isActive ? 600 : 400, transition: 'all 0.15s', whiteSpace: 'nowrap',
            })}>
              <Icon size={18} style={{ flexShrink: 0 }} />
              {sidebarOpen && label}
            </NavLink>
          ))}
        </nav>
        <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'none', border: 'none', borderTop: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}>
          <LogOut size={18} style={{ flexShrink: 0 }} />
          {sidebarOpen && 'Sign Out'}
        </button>
      </aside>
      <main style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>{children}</main>
    </div>
  );
}

function LayoutInner() {
  const isMobile = useIsMobile();
  const content = (
    <>
      <MigrationBanner />
      <Outlet />
    </>
  );
  return isMobile ? <MobileLayout>{content}</MobileLayout> : <DesktopLayout>{content}</DesktopLayout>;
}

export default function MainLayout() {
  return <ShiftProvider><LayoutInner /></ShiftProvider>;
}
